import { Box, Skeleton, Text, VStack } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import FlexSearch from "flexsearch";
import { memo, useCallback } from "react";
import { FeaturedSection } from "./featured-section/table";
import { TableContent } from "./tables/table-content";
import { TableRow } from "./utils/tables-metadata";
import { useSharedContext } from "../shared-logic/shared-context";

export type PreviewSectionCounts = {
  sectionCounts: {
    reports: number;
    documents: number;
  };
};

export type IndexedTableData = {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  index: FlexSearch.Index;
  indexingPromises: Promise<FlexSearch.Index>[];
  isEmptySection: boolean;
};

export type FilteredTableData = {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  index: FlexSearch.Index;
  indexingPromises: Promise<FlexSearch.Index>[];
  filteredRows: TableRow[];
  isEmptySection: boolean;
};

interface PatientOverviewContentV2Props {
  fhirSdk: FhirBundleSdk | undefined;
  children: React.ReactNode;
  isLoadingData?: boolean;
  filterQuery?: string;
  filteredTableData: FilteredTableData[];
  onFeaturedDataChange?: (hasFeaturedData: boolean) => void;
  onRowSelect: (rowState: SelectedRowState) => void;
  onPdfView: (docId: string, docFilename?: string, initialSearchQuery?: string) => void;
}

export type SelectedRowState = {
  row: TableRow;
  tableType: string;
  suspectsContext?: string;
};

function PatientOverviewContentV2({
  fhirSdk,
  children,
  filterQuery = "",
  isLoadingData = false,
  filteredTableData,
  onFeaturedDataChange,
  onRowSelect,
  onPdfView,
}: PatientOverviewContentV2Props) {
  const {
    sharedState: { isAmazonUser },
  } = useSharedContext();
  /**
   * Handles row click from any table.
   * Notifies parent to handle side panel opening and selection.
   */
  const handleRowClick = useCallback(
    (row: TableRow, tableType: string, suspectsContext?: string) => {
      onRowSelect({ row, tableType, suspectsContext });
    },
    [onRowSelect]
  );

  if (!fhirSdk || isLoadingData) {
    return (
      <Box minH="100vh" backgroundColor="bg.canvas">
        <VStack align="stretch" gap={4}>
          <Skeleton height="400px" borderRadius={10} />
          <Skeleton height="300px" borderRadius={10} />
          <Skeleton height="350px" borderRadius={10} />
          <Skeleton height="250px" borderRadius={10} />
        </VStack>
      </Box>
    );
  }

  return (
    <>
      <Box backgroundColor="bg.canvas">
        <VStack align="stretch">
          <Box id="ai-summary">{children}</Box>
          <VStack align="stretch">
            {isAmazonUser && (
              <Box id="featured">
                <FeaturedSection
                  filteredTableData={filteredTableData}
                  onRowClick={handleRowClick}
                  fhirSdk={fhirSdk}
                  filterQuery={filterQuery}
                  onPdfView={onPdfView}
                  onFeaturedDataChange={onFeaturedDataChange}
                />
              </Box>
            )}

            {filteredTableData.every(table => table.isEmptySection) && (
              <Box
                py={10}
                px={8}
                borderRadius="lg"
                backgroundColor="bg.subtle"
                border="1px"
                borderColor="border"
                textAlign="center"
              >
                <Text fontWeight="semibold" color="fg.muted" fontSize="lg">
                  No results found
                </Text>
                <Text color="fg.subtle" fontSize="sm" mt={2}>
                  Try different keywords, filters, or review your spelling.
                </Text>
              </Box>
            )}

            {filteredTableData.map(({ tableType, displayName, filteredRows, isEmptySection }) => {
              // If searching and there are no rows, don't show the section
              if (isEmptySection && filterQuery.trim()) {
                return null;
              }

              // Show an empty placeholder if the section has no rows
              if (isEmptySection) {
                return (
                  <EmptyResourcePlaceholder
                    key={tableType}
                    displayName={displayName}
                    tableType={tableType}
                  />
                );
              }

              return (
                <Box key={tableType} id={tableType}>
                  <TableContent
                    tableType={tableType}
                    displayName={displayName}
                    filteredRows={filteredRows}
                    fhirSdk={fhirSdk}
                    filterQuery={filterQuery}
                    handleRowClick={handleRowClick}
                    handlePdfView={onPdfView}
                  />
                </Box>
              );
            })}
          </VStack>
        </VStack>
      </Box>
    </>
  );
}

/**
 * Displays a placeholder when there are no rows for a resource type
 */
function EmptyResourcePlaceholder({
  displayName,
  tableType,
}: {
  displayName: string;
  tableType: string;
}) {
  return (
    <Box py={1} id={tableType}>
      <Text color="fg.muted">{displayName} (0)</Text>
    </Box>
  );
}

export default memo(PatientOverviewContentV2);
